/**
 * The /agent pages a token can open, from agent-action-respond's page_links
 * (minted server side, so an owner on a link gets the same working links a
 * member does), asked once per token per visit: the top bar's Home pill and
 * the page under it share the answer instead of asking twice.
 *
 * The Home pill needs it (live QA, 21 Sep 26): a page opened with a typed
 * token (a new_post link, say) used to send Home to /agent/home/<that token>,
 * where the home half works and the All locations roll-up answers 403.
 * links.home is the location's real home link.
 */
import { resolveAspectHrefs, type TokenOrCaller } from '@/components/gbp-agent/agentLinks';

// Inside the app each answer holds the signed-in person's own /app/<slug>
// addresses (clean addresses PR 2), keyed only by the location, so it is
// dropped whenever the person changes (resetPageLinks, from clearAppCaches).
const answers = new Map<string, Promise<Record<string, string>>>();

/** One answer per token, or per location inside the app. */
function keyOf(who: TokenOrCaller): string {
  if (typeof who === 'string') return `token:${who}`;
  return who.mode === 'app' ? `app:${who.locationId ?? ''}` : `token:${who.token ?? ''}`;
}

export function pageLinksFor(who: TokenOrCaller): Promise<Record<string, string>> {
  const key = keyOf(who);
  let answer = answers.get(key);
  if (!answer) {
    answer = resolveAspectHrefs(who);
    answers.set(key, answer);
    // resolveAspectHrefs settles on what it could resolve; a thrown error
    // (none expected) must not pin a failure for the rest of the visit. Only
    // its own entry: a late failure from before a reset keeps the next one.
    const held = answer;
    held.catch(() => {
      if (answers.get(key) === held) answers.delete(key);
    });
  }
  return answer;
}

/** Sign out, or another person signed in (clearAppCaches): no answer of the last person's stays. Also a test seam. */
export function resetPageLinks(): void {
  answers.clear();
}
